import { createClient } from '@supabase/supabase-js';

// KONFIGURATION: Standard-Werte aus der Projekt-Konfiguration
// Diese Werte werden verwendet, wenn im LocalStorage nichts anderes steht.
const DEFAULT_SUPABASE_URL = 'https://cjtarocrupqnbwlrujpx.supabase.co';
const DEFAULT_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNqdGFyb2NydXBxbmJ3bHJ1anB4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjUwNTEzNTcsImV4cCI6MjA4MDYyNzM1N30.r-LZfkjTffnqgrlzudE5t4aiS8xT1mI-Jo7h0A02my0';

// Prüfe LocalStorage für Benutzer-Overrides (wichtig für Admin-Bereich Änderungen)
const storedUrl = typeof window !== 'undefined' ? localStorage.getItem('cbuy_supabase_url') : null;
const storedKey = typeof window !== 'undefined' ? localStorage.getItem('cbuy_supabase_key') : null;

// Nutze gespeicherte Werte ODER die korrekten Defaults
const supabaseUrl = storedUrl || DEFAULT_SUPABASE_URL;
const supabaseAnonKey = storedKey || DEFAULT_SUPABASE_ANON_KEY;

// Tabellen-Namen (ohne Suffixe, wie in Migrations definiert)
export const DB_TABLES = {
  TERMINMARKT: 'terminmarkt_providers',
  SPOTMARKT: 'spotmarkt_providers',
  PROVISIONS: 'provisions_data',
  FILES: 'file_metadata',
  SETTINGS: 'app_settings'
};

console.log('🔌 Supabase Config initiiert mit URL:', supabaseUrl);

// Prüfen, ob eine gültige Konfiguration vorliegt
const isSupabaseConfigured = () => {
  return supabaseUrl && supabaseAnonKey && supabaseUrl.startsWith('http');
};

// Supabase Client initialisieren
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  }
});

// WICHTIG: Definiere die gültigen Spalten für jede Tabelle
// Diese Spalten MÜSSEN mit der Datenbank-Schema übereinstimmen
const VALID_PROVIDER_COLUMNS = [
  'id',
  'anbieter_id',
  'created_at',
  'evu',
  'marktart',
  'strom_slp',
  'strom_rlm',
  'gas_slp',
  'gas_rlm',
  'verbrauchsgrenzen_strom',
  'verbrauchsgrenzen_gas',
  'bestellungsrelevante_unterlagen',
  'pflichtangaben',
  'provisionsgrenze',
  'auszahlung',
  'vertragsunterlagen_nach_abschluss',
  'ausgeschlossene_branchen',
  'bemerkung',
  'bonitaetsgrenze',
  'mehr_mindermengen_in_prozent',
  'kuendigungsfrist',
  'verlaengerung_aktiv_passiv',
  'buendelung',
  'vollmachten_erlaubt',
  'wechsel_von_fest_zu_spot',
  'wechsel_von_spot_zu_fest',
  'provision_spot',
  'provision_festpreis',
  'provision_tranche',
  'provision_bemerkung_festpreis',
  'quelle',
  'lastUpdated',
  'bietet_strom',
  'bietet_gas',
  'name',
  'energyType',
  'marketType'
];

// Filterfunktion: Entfernt alle Spalten, die nicht in der DB existieren
const filterValidColumns = (record) => {
  const filtered = {};
  VALID_PROVIDER_COLUMNS.forEach(col => {
    if (col in record && record[col] !== undefined && record[col] !== null) {
      filtered[col] = record[col];
    }
  });
  return filtered;
};

// Hilfsfunktionen für Datenbank-Operationen
export const supabaseHelpers = {
  // Terminmarkt-Daten
  async getTerminmarktProviders() {
    const { data, error } = await supabase
      .from(DB_TABLES.TERMINMARKT)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async saveTerminmarktProviders(providers) {
    if (!providers || providers.length === 0) return;
    
    try {
      // Alte Daten löschen
      await supabase.from(DB_TABLES.TERMINMARKT).delete().gte('created_at', new Date(0).toISOString());
      
      // WICHTIG: Filtere nur gültige Spalten bevor du insertierst
      const cleanedProviders = providers.map(p => filterValidColumns(p));
      
      // Neue Daten speichern
      const { data, error } = await supabase
        .from(DB_TABLES.TERMINMARKT)
        .insert(cleanedProviders)
        .select();
      
      if (error) throw error;
      console.log('✅ Terminmarkt-Daten erfolgreich gespeichert');
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Speichern von Terminmarkt-Daten:', error);
      throw error;
    }
  },

  // Spotmarkt-Daten
  async getSpotmarktProviders() {
    const { data, error } = await supabase
      .from(DB_TABLES.SPOTMARKT)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async saveSpotmarktProviders(providers) {
    if (!providers || providers.length === 0) return;
    
    try {
      // Alte Daten löschen
      await supabase.from(DB_TABLES.SPOTMARKT).delete().gte('created_at', new Date(0).toISOString());
      
      // WICHTIG: Filtere nur gültige Spalten bevor du insertierst
      const cleanedProviders = providers.map(p => filterValidColumns(p));
      
      // Neue Daten speichern
      const { data, error } = await supabase
        .from(DB_TABLES.SPOTMARKT)
        .insert(cleanedProviders)
        .select();
      
      if (error) throw error;
      console.log('✅ Spotmarkt-Daten erfolgreich gespeichert');
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Speichern von Spotmarkt-Daten:', error);
      throw error;
    }
  },

  // Provisions-Daten
  async getProvisionsData() {
    const { data, error } = await supabase
      .from(DB_TABLES.PROVISIONS)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async saveProvisionsData(provisions) {
    if (!provisions || provisions.length === 0) return;
    
    try {
      // Alte Daten löschen
      await supabase.from(DB_TABLES.PROVISIONS).delete().gte('created_at', new Date(0).toISOString());
      
      // WICHTIG: Filtere nur gültige Spalten bevor du insertierst
      const cleanedProvisions = provisions.map(p => filterValidColumns(p));
      
      // Neue Daten speichern
      const { data, error } = await supabase
        .from(DB_TABLES.PROVISIONS)
        .insert(cleanedProvisions)
        .select();
      
      if (error) throw error;
      console.log('✅ Provisions-Daten erfolgreich gespeichert');
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Speichern von Provisions-Daten:', error);
      throw error;
    }
  },

  // Datei-Metadaten
  async getFileMetadata() {
    const { data, error } = await supabase
      .from(DB_TABLES.FILES)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async saveFileMetadata(fileInfo) {
    const { data, error } = await supabase.from(DB_TABLES.FILES).insert(fileInfo);
    if (error) throw error;
    return data;
  },

  // App-Einstellungen
  async getSettings(category) {
    const { data, error } = await supabase
      .from(DB_TABLES.SETTINGS)
      .select('*')
      .eq('category', category);
    if (error) throw error;
    return data;
  },

  async saveSetting(setting) {
    const { data, error } = await supabase.from(DB_TABLES.SETTINGS).insert(setting);
    if (error) throw error;
    return data;
  },

  // Testverbindung
  async testConnection() {
    try {
      console.log('🔄 Teste Supabase Verbindung...');
      // Versuche einen einfachen Lesezugriff
      const { data, error } = await supabase
        .from(DB_TABLES.SETTINGS)
        .select('count')
        .limit(1);
      
      if (error) {
        if (error.code === '42P01') {
            console.log('✅ Supabase connected (tables missing)');
            return { success: true, message: 'Connected but tables missing' };
        }
        
        console.error('❌ Supabase connection test failed:', error);
        return { success: false, error: error.message };
      }
      console.log('✅ Supabase connection successful');
      return { success: true, message: 'Supabase connection successful' };
    } catch (error) {
      console.error('❌ Supabase connection test critical error:', error);
      return { success: false, error: error.message };
    }
  }
};

export { isSupabaseConfigured };
export default supabase;